<template>
  <div class="options-list mt-3 px-3 py-1">
    <!-- Saved POIs Section -->
    <div v-if="canShowSavedPOIs" class="saved-pois-section mb-3">
      <div class="saved-pois-item d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div class="saved-pois-icon-container">
            <i class="material-icons">star</i>
          </div>
          <span class="label-text ml-3">{{ $t('base_map_dialog.base_map_data_po_is_tab.hike_highlights') }}</span>
        </div>
        <div class="d-flex align-items-center">
          <label class="toggle-switch ml-3">
            <input type="checkbox" v-model="savedPOIsEnabled" @change="toggleSavedPOIs">
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>

    <!-- Waypoints Section -->
    <div v-if="canShowHikeHighlights" class="waypoints-section mb-3">
      <div class="waypoints-item d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div class="waypoints-icon-container">
            <i class="material-icons">flag</i>
          </div>
          <span class="waypoints-label ml-3">{{ $t('base_map_dialog.base_map_data_po_is_tab.hike_highlights') }}</span>
        </div>
        <div class="d-flex align-items-center">
          <label class="toggle-switch ml-3">
            <input type="checkbox" v-model="hikeHighlightsEnabled" @change="toggleHikeHighlights">
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>

    <hr v-if="canShowHikeHighlights" class="my-4">

    <p v-if="canShowHikeHighlights" class="display-5 mb-3"><strong>{{ $t('base_map_dialog.base_map_data_po_is_tab.points_of_interest') }}</strong></p>

    <div class="form-group">
      <input type="text" class="form-control rounded-pill my-2" :placeholder="$t('base_map_dialog.base_map_data_po_is_tab.filter_by_type')" v-model="searchQuery"/>
    </div>
    <!-- Enable All / Clear All buttons -->
    <div class="w-100 d-flex justify-content-between">
      <button
        class="btn btn-text d-flex align-items-center justify-content-center p-0 py-2"
        @click="enableAllLayers"
      >
        <i class="material-icons md-18">done_all</i>
        <span class="ml-2 text-nowrap">{{ $t('base_map_dialog.base_map_data_po_is_tab.enable_all') }}</span>
      </button>
      <button
        class="btn btn-text d-flex align-items-center justify-content-center p-0 py-2"
        @click="clearAllLayers"
      >
        <i class="material-icons md-18">clear_all</i>
        <span class="ml-2 text-nowrap">{{ $t('common.clear_all') }}</span>
      </button>
    </div>
    <div
      class="option-item"
      v-for="poi in filteredPoiList"
      :key="poi.layerName"
    >
      <input
        type="checkbox"
        :id="`toggle-${poi.layerName}`"
        :checked="isPoiActive(poi)"
        @change="togglePoi(poi)"
        style="display: none"
      />

      <label
        class="custom-checkbox d-flex justify-content-between"
        role="switch"
        :aria-checked="isPoiActive(poi)"
        @click="togglePoi(poi)"
      >
        <div class="d-flex justify-content-between align-items-center">
          <img
            :src="poi.getPublicImageIcon()"
            :alt="poi.prettyName()"
            class="poi-icon"
            :class="{ selected: isPoiActive(poi) }"
            width="25"
          />
          <span class="ml-2 label-text">{{ poi.prettyName() }}</span>
        </div>

        <svg xmlns="http://www.w3.org/2000/svg" width="42" height="21" viewBox="0 0 42 21" fill="none">
          <rect
            width="42"
            height="21"
            rx="10.5"
            :fill="isPoiActive(poi) ? poi.getColor() : '#CCC'"
          />
          <circle
            class="switch-circle"
            :class="{ 'switch-circle-active': isPoiActive(poi) }"
            cx="11.5"
            cy="10.5"
            r="8.5"
            fill="white"
          />
        </svg>
      </label>
      <hr>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import graph_client from "../../../utils/graphql/graph_client";
import {GetHikeWaypointsCount} from "../../../utils/graphql/hikes-graph";
import emitter from "../../../utils/bus";

export default {
  name: 'MapDataPOIsTab',
  data() {
    const initialHighlightsEnabled = (typeof window !== 'undefined' && window.hikeHighlightsVisible !== undefined)
      ? !!window.hikeHighlightsVisible
      : true;
    return {
      searchQuery: '',
      savedPOIsEnabled: initialHighlightsEnabled,
      hikeHighlightsEnabled: initialHighlightsEnabled,
      canShowHikeHighlights: false,
      canShowSavedPOIs: false,
    };
  },
  mounted() {
    if (window.hike_id) {
      graph_client.request(GetHikeWaypointsCount, {
        id: window.hike_id
      }).then(response => {
        if (response.hike) {
          if (response.hike.waypointsCount > 0) {
            this.canShowHikeHighlights = true;
          }
          if (response.hike.hikeHighlightsCount > 0) {
            this.canShowSavedPOIs = true;
          }
        }
      }).catch(error => {
        console.error('Error fetching hike waypoints count:', error);
      });
    }
    window.hikeHighlightsVisible = this.savedPOIsEnabled;
    emitter.emit('hike-highlights:toggle', this.savedPOIsEnabled);
  },
  computed: {
    ...mapState('mapDataPOIs', ['poiList', 'activatedPois']),
    ...mapGetters('mapDataPOIs', ['isPoiActive']),

    filteredPoiList() {
      if (!this.searchQuery) {
        return this.poiList;
      }
      return this.poiList.filter(poi => {
        const searchTerm = this.searchQuery.toLowerCase();
        const poiName = poi.prettyName()
          .toLowerCase();
        return poiName.includes(searchTerm);
      });
    }
  },
  methods: {
    ...mapActions('mapDataPOIs', ['togglePoi', 'initActivePois', 'viewCreated', 'beforeViewDestroyed', 'clearAllPoi', 'addAllPois']),
    clearAllLayers() {
      this.clearAllPoi();
    },
    enableAllLayers() {
      this.addAllPois();
    },
    toggleSavedPOIs() {
      const enabled = !!this.savedPOIsEnabled;
      this.hikeHighlightsEnabled = enabled;
      window.hikeHighlightsVisible = enabled;
      emitter.emit('hike-highlights:toggle', enabled);
    },
    addSavedPOI() {
      // TODO: Implement add saved POI functionality
      console.log('Add saved POI');
    },
    showMoreOptions() {
      // TODO: Implement more options functionality
      console.log('Show more options');
    },
    toggleHikeHighlights() {
      const enabled = !!this.hikeHighlightsEnabled;
      this.savedPOIsEnabled = enabled;
      window.hikeHighlightsVisible = enabled;
      emitter.emit('hike-highlights:toggle', enabled);
    },
    addWaypoint() {
      // TODO: Implement add waypoint functionality
      console.log('Add waypoint');
    },
    showWaypointOptions() {
      // TODO: Implement waypoint options functionality
      console.log('Show waypoint options');
    }
  },
  created() {
    this.viewCreated();
  },
  beforeDestroy() {
    this.beforeViewDestroyed();
  },
};
</script>

<style scoped>
.options-list {
  padding: 0;
  margin: 0;
}

.option-item {
  padding: 10px 0;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.label-text {
  font-size: 1em;
  font-weight: 500;
}

.switch-circle {
  transition: transform 0.3s ease;
}

.switch-circle-active {
  transform: translateX(19px);
}

/* Optional search input styling */
.form-control {
  padding: 10px 19px;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out;
}

/* add to <style scoped> ― or merge with your existing rules */
.btn-clear-all {
  width: 100%;
  background-color: #ff5c5c;          /* soft alert red */
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  padding: 10px 12px;
  transition: background-color 0.2s ease-in-out, transform 0.15s ease-in-out;
}

.btn-clear-all:hover {
  background-color: #ff7070;          /* slightly lighter on hover */
}

.btn-clear-all:active {
  transform: scale(0.97);
}

.btn-clear-all .material-icons {
  font-size: 18px;   /* same visual size as before */
  line-height: 1;
  flex-shrink: 0;
}

.saved-pois-item {
  min-height: 48px;
}

.saved-pois-icon-container {
  width: 31px;
  height: 31px;
  background-color: #f5dd42;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.saved-pois-icon-container i {
  color: #000;
  font-size: 20px;
}

.saved-pois-label {
  font-size: 16px;
  font-weight: 500;
  color: #fff;
}

.btn-icon-small {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-icon-small:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.btn-icon-small i {
  font-size: 20px;
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #17181d;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #f5dd42;
}

input:checked + .toggle-slider:before {
  transform: translateX(18px);
}

/* Waypoints Section Styles */
.waypoints-section {
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 12px 16px;
}

.waypoints-item {
  min-height: 48px;
}

.waypoints-icon-container {
  width: 40px;
  height: 40px;
  background-color: #00bcd4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.waypoints-icon-container i {
  color: #000;
  font-size: 20px;
}

.waypoints-label {
  font-size: 16px;
  font-weight: 500;
  color: #fff;
}

</style>
